/**
 * useMultiplayer — central multiplayer state hook.
 *
 * Manages WebSocket connection, room lifecycle, cooldown-based game state,
 * and graph updates from server broadcasts. Analogous to useGame.js for solo.
 *
 * All players act simultaneously with a per-player cooldown between actions.
 * The server is the graph authority — clients receive diffs and apply them.
 *
 * Lazy connection: no socket is created until createRoom() or joinRoom()
 * is called, so solo play never opens a WebSocket.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getSocket, connectSocket, disconnectSocket } from '../services/socket';

const VICTORY_DELAY = 800;

export default function useMultiplayer() {
  // Connection
  const [connected, setConnected] = useState(false);

  // Room
  const [roomCode, setRoomCode] = useState(null);
  const [players, setPlayers] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [roomStatus, setRoomStatus] = useState(null); // "lobby" | "playing" | "finished"
  const [difficulty, setDifficulty] = useState(50);
  const [previewWords, setPreviewWords] = useState(null);

  // Game
  const [targetWord, setTargetWord] = useState(null);
  const [initialWord, setInitialWord] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);

  // Cooldown — per-player cooldown replaces turn system
  const [onCooldown, setOnCooldown] = useState(false);
  const [cooldownEnd, setCooldownEnd] = useState(null);
  const [cooldownDuration, setCooldownDuration] = useState(3);
  const cooldownTimer = useRef(null);

  // Bombs — map of playerId → remaining bombs
  const [bombs, setBombs] = useState({});

  // Victory
  const [gameComplete, setGameComplete] = useState(false);
  const [victoryPending, setVictoryPending] = useState(false);
  const [winningWord, setWinningWord] = useState(null);
  const [winnerId, setWinnerId] = useState(null);
  const [winnerName, setWinnerName] = useState(null);

  // Graph display (same signals as useGame for WordGraph compatibility)
  const [selectedWord, setSelectedWord] = useState(null);
  const [lastExpandedWord, setLastExpandedWord] = useState(null);
  const [expansionFocusIds, setExpansionFocusIds] = useState(null);
  const [lastCombineResult, setLastCombineResult] = useState(null);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const victoryTimeout = useRef(null);
  const mySocketId = useRef(null);
  const listenersAttached = useRef(false);

  // Clear error after 5 seconds
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(t);
  }, [error]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (victoryTimeout.current) clearTimeout(victoryTimeout.current);
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
      teardownListeners();
    };
  }, []);

  // -----------------------------------------------------------------
  // Cooldown helper
  // -----------------------------------------------------------------

  function startCooldown(durationSeconds) {
    setOnCooldown(true);
    const endTime = Date.now() + durationSeconds * 1000;
    setCooldownEnd(endTime);
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    cooldownTimer.current = setTimeout(() => {
      setOnCooldown(false);
      setCooldownEnd(null);
    }, durationSeconds * 1000);
  }

  // -----------------------------------------------------------------
  // Socket event handlers — attached lazily on first connect
  // -----------------------------------------------------------------

  function resetState() {
    setRoomCode(null);
    setPlayers([]);
    setIsHost(false);
    setRoomStatus(null);
    setDifficulty(50);
    setPreviewWords(null);
    setTargetWord(null);
    setInitialWord(null);
    setNodes([]);
    setLinks([]);
    setOnCooldown(false);
    setCooldownEnd(null);
    setBombs({});
    setGameComplete(false);
    setVictoryPending(false);
    setWinningWord(null);
    setWinnerId(null);
    setWinnerName(null);
    setSelectedWord(null);
    setLastExpandedWord(null);
    setExpansionFocusIds(null);
    setLastCombineResult(null);
    setLoading(false);
    setError(null);
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
  }

  /** Attach all socket listeners. Safe to call multiple times. */
  function setupListeners() {
    if (listenersAttached.current) return;
    listenersAttached.current = true;

    const socket = getSocket();

    socket.on("connect", () => {
      setConnected(true);
      mySocketId.current = socket.id;
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    // Room events
    socket.on("room:created", ({ roomCode: code, roomState }) => {
      setRoomCode(code);
      setPlayers(roomState.players);
      setIsHost(true);
      setRoomStatus("lobby");
      setDifficulty(roomState.difficulty ?? 50);
      setPreviewWords(roomState.previewWords ?? null);
    });

    socket.on("room:joined", ({ roomState }) => {
      setRoomCode(roomState.roomCode);
      setPlayers(roomState.players);
      setIsHost(roomState.hostId === socket.id);
      setRoomStatus("lobby");
      setDifficulty(roomState.difficulty ?? 50);
      setPreviewWords(roomState.previewWords ?? null);
    });

    socket.on("room:difficulty_changed", ({ difficulty: d, previewWords: pw }) => {
      setDifficulty(d);
      if (pw) setPreviewWords(pw);
    });

    socket.on("room:player_joined", ({ players: updatedPlayers }) => {
      setPlayers(updatedPlayers);
    });

    socket.on("room:player_left", ({ players: updatedPlayers, newHostId }) => {
      setPlayers(updatedPlayers);
      if (newHostId === socket.id) {
        setIsHost(true);
      }
    });

    socket.on("room:disbanded", () => {
      resetState();
      setError("Room was disbanded");
    });

    socket.on("room:words_preview", ({ previewWords: pw }) => {
      if (pw) setPreviewWords(pw);
    });

    socket.on("room:lobby_reset", ({ roomState }) => {
      setRoomStatus("lobby");
      setPlayers(roomState.players);
      setIsHost(roomState.hostId === socket.id);
      setDifficulty(roomState.difficulty ?? 50);
      setPreviewWords(roomState.previewWords ?? null);
      setBombs({});
      setGameComplete(false);
      setVictoryPending(false);
      setNodes([]);
      setLinks([]);
      setTargetWord(null);
      setInitialWord(null);
      setWinningWord(null);
      setWinnerId(null);
      setWinnerName(null);
      setSelectedWord(null);
      setLastExpandedWord(null);
      setExpansionFocusIds(null);
      setLastCombineResult(null);
      setOnCooldown(false);
      setCooldownEnd(null);
      setLoading(false);
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    });

    socket.on("room:error", ({ message }) => {
      setError(message);
      setLoading(false);
    });

    // Game events
    socket.on("game:started", ({ gameState }) => {
      setRoomStatus("playing");
      setInitialWord(gameState.initialWord);
      setTargetWord(gameState.targetWord);
      setNodes(gameState.nodes);
      setLinks(gameState.links);
      setCooldownDuration(gameState.cooldownDuration || 3);
      setBombs(gameState.bombs || {});
      setOnCooldown(false);
      setCooldownEnd(null);
      setGameComplete(false);
      setVictoryPending(false);
      setWinningWord(null);
      setWinnerId(null);
      setWinnerName(null);
      setSelectedWord(null);
      setLastExpandedWord(null);
      setExpansionFocusIds(null);
      setLastCombineResult(null);
    });

    socket.on("game:expanded", (data) => {
      const { word, newNodes, newLinks, expandedNodeId, playerId, cooldownDuration: cd } = data;
      const isMe = playerId === socket.id;

      setNodes(prev => {
        const updated = prev.map(n =>
          n.id === expandedNodeId ? { ...n, isExpanded: true } : n,
        );
        return [...updated, ...newNodes];
      });
      setLinks(prev => [...prev, ...newLinks]);

      // Only adjust viewport / highlights for the player who acted
      if (isMe) {
        setLastExpandedWord(word);
        setLastCombineResult(null);
        setExpansionFocusIds(new Set([word, ...newNodes.map(n => n.id)]));
        setLoading(false);
        startCooldown(cd || 3);
      }
    });

    socket.on("game:combined", (data) => {
      const { word1, word2, combinedWords, newNodes: newNds, newLinks: newLnks, playerId, cooldownDuration: cd } = data;
      const isMe = playerId === socket.id;

      setNodes(prev => [...prev, ...newNds]);
      setLinks(prev => [...prev, ...newLnks]);

      // Only adjust viewport / highlights for the player who acted
      if (isMe) {
        setLastExpandedWord(null);
        setLastCombineResult({ parents: [word1, word2], results: combinedWords });
        setExpansionFocusIds(new Set(combinedWords));
        setLoading(false);
        startCooldown(cd || 3);
      }
    });

    socket.on("game:bombed", (data) => {
      const { playerId, destroyedNodes, bombsRemaining } = data;
      const destroyed = new Set(destroyedNodes);

      setNodes(prev => prev.filter(n => !destroyed.has(n.id)));
      setLinks(prev => prev.filter(l => {
        const sId = typeof l.source === 'object' ? l.source.id : l.source;
        const tId = typeof l.target === 'object' ? l.target.id : l.target;
        return !destroyed.has(sId) && !destroyed.has(tId);
      }));
      setBombs(prev => ({ ...prev, [playerId]: bombsRemaining }));

      // Clear any stale expansion/combine highlights
      setLastExpandedWord(null);
      setLastCombineResult(null);
      setExpansionFocusIds(null);
    });

    socket.on("game:victory", ({ winnerId: wid, winnerName: wname, winningWord: wword }) => {
      setWinningWord(wword);
      setWinnerId(wid);
      setWinnerName(wname);
      setVictoryPending(true);
      setLoading(false);

      victoryTimeout.current = setTimeout(() => {
        setGameComplete(true);
        setVictoryPending(false);
        setRoomStatus("finished");
      }, VICTORY_DELAY);
    });

    socket.on("game:error", ({ message }) => {
      setError(message);
      setLoading(false);
    });
  }

  /** Remove our listeners and reset the attached flag. */
  function teardownListeners() {
    if (!listenersAttached.current) return;
    listenersAttached.current = false;

    const socket = getSocket();
    const events = [
      "connect", "disconnect",
      "room:created", "room:joined", "room:player_joined", "room:player_left",
      "room:disbanded", "room:lobby_reset", "room:difficulty_changed", "room:words_preview", "room:error",
      "game:started", "game:expanded", "game:combined",
      "game:bombed", "game:victory", "game:error",
    ];
    for (const event of events) {
      socket.off(event);
    }
  }

  // -----------------------------------------------------------------
  // Actions (emitters)
  // -----------------------------------------------------------------

  /** Connect socket and attach listeners (idempotent). */
  function initSocket() {
    setupListeners();
    connectSocket();
  }

  const createRoom = useCallback((playerName) => {
    initSocket();
    getSocket().emit("room:create", { playerName });
  }, []);

  const joinRoom = useCallback((code, playerName) => {
    initSocket();
    getSocket().emit("room:join", { roomCode: code.toUpperCase(), playerName });
  }, []);

  const leaveRoom = useCallback(() => {
    getSocket().emit("room:leave", {});
    disconnectSocket();
    teardownListeners();
    resetState();
  }, []);

  const setRoomDifficulty = useCallback((d) => {
    setDifficulty(d);
    getSocket().emit("room:set_difficulty", { difficulty: d });
  }, []);

  const rerollWords = useCallback(() => {
    getSocket().emit("room:reroll_words", {});
  }, []);

  const startGame = useCallback(() => {
    getSocket().emit("room:start_game", {});
  }, []);

  const expandWord = useCallback((word) => {
    if (onCooldown || loading) return;
    setLoading(true);
    setSelectedWord(word);
    getSocket().emit("game:expand", { word });
  }, [onCooldown, loading]);

  const combineWords = useCallback((word1, word2) => {
    if (onCooldown || loading) return;
    setLoading(true);
    getSocket().emit("game:combine", { word1, word2 });
  }, [onCooldown, loading]);

  const useBomb = useCallback((word) => {
    const myId = mySocketId.current;
    if (!myId || (bombs[myId] || 0) <= 0) return;
    getSocket().emit("game:bomb", { word });
  }, [bombs]);

  const returnToLobby = useCallback(() => {
    getSocket().emit("room:return_to_lobby", {});
  }, []);

  const handleNodeClick = useCallback((word) => {
    setSelectedWord(word);
    expandWord(word);
  }, [expandWord]);

  return {
    // Connection
    connected,
    mySocketId: mySocketId.current,

    // Room
    roomCode, players, isHost, roomStatus, difficulty, previewWords,

    // Game
    targetWord, initialWord,
    nodes, links,
    onCooldown, cooldownEnd, cooldownDuration,
    bombs,

    // Victory
    gameComplete, victoryPending, winningWord,
    winnerId, winnerName,

    // Graph display
    selectedWord, lastExpandedWord,
    expansionFocusIds, lastCombineResult,

    // UI
    loading, error,

    // Actions
    createRoom, joinRoom, leaveRoom,
    setRoomDifficulty, rerollWords, startGame, handleNodeClick, combineWords,
    useBomb, returnToLobby,
  };
}
